import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, Signal, computed, inject, signal } from '@angular/core';
import { ApiResponse } from '../models/api-response';
import { AddReportRequest, Report, ReportFilterRequest, ReportStatus } from '../models/report';
import { ReportType } from '../models/report-type';
import { Client } from '../models/client';
import { map, Observable, Subject } from 'rxjs';
import { ReportSectionData } from '../models/report-sections';
import { ReportStatusRequest } from '../models/report-status-request';
import { CommentDeleteRequest, CommentRequest } from '../models/comment-request';
import { NewSectionRequest } from '../models/new-section-request';
import { SectionValidationRequest } from '../models/section-validation-request';
import { SnackbarService } from './snackbar.service';
import { tap } from 'rxjs/operators';
import { States } from '../models/states';
import { API_ENDPOINTS, buildEndpoint } from '../api-endpoint';
import { Locations } from '../models/Locations';
import { GetProjectFilesRequest } from '../models/get-project-files-request';
import { RuntimeConfigService } from '../config/runtime-config.service';
import { Reviewer, ReviewerRequest } from '../models/reviewer-response';
import { VersionToggleService } from './version-toggle.service';

@Injectable({
  providedIn: 'root',
})
export class ReportService {
  private snackbar = inject(SnackbarService);
  private recentReportsSignal = signal<Report[]>([]);
  private reportTypesSignal = signal<ReportType[]>([]);
  private clientsSignal = signal<Client[]>([]);
  private stateSignal = signal<States[]>([]);
  private locationSignal = signal<Locations[]>([]);
  private baseApiUrl: string;
  private apiVersion: string;

  constructor(
    private runtimeConfigService: RuntimeConfigService,
    private versionToggleService: VersionToggleService,
    private http: HttpClient
  ) {
    this.baseApiUrl = this.runtimeConfigService.apiUrl;
    this.apiVersion = this.runtimeConfigService.apiVersion;
  }

  private refreshReportsSource = new Subject<void>();
  refreshChartData$ = this.refreshReportsSource.asObservable();

  getApiUrl(apiEndpoint: string): string {
    return buildEndpoint(this.baseApiUrl, this.apiVersion, 'reportservice', apiEndpoint);
  }

  getRecentReports(): Signal<Report[]> {
    return computed(() => this.recentReportsSignal());
  }

  loadReports(): void {
    this.refreshReportsSource.next();
    const request: ReportFilterRequest = {
      PageNumber: 0,
      PageSize: 0
     };
    const apiUrl = this.getApiUrl(API_ENDPOINTS.REPORT_SERVICE.REPORTS.RecentReports);
    this.http
      .post<ApiResponse<Report[]>>(apiUrl,request)
      .pipe(
        tap((response: ApiResponse<Report[]>) => {
          if (!response.success) {
            this.snackbar.showCustom({
              type: 'error',
              title: 'Error',
              message: response.message,
            });
          }
        }),
        map((response: ApiResponse<Report[]>) => response.data)
      )
      .subscribe({
        next: (reports) => this.recentReportsSignal.set(reports),
        error: (err) => {
         
        },
      });
  }

  getStatusDisplay(status: ReportStatus): string {
    return ReportStatus[status];
  }

  deleteReport(reportId: number): Observable<ApiResponse<boolean>> {
    const apiUrl = this.getApiUrl(API_ENDPOINTS.REPORT_SERVICE.REPORTS.DeleteReport).replace(
      '{reportId}',
      reportId.toString()
    );
    return this.http.delete<ApiResponse<boolean>>(apiUrl);
  }

  findReportsByType(reportTypeId: number, projectId: number, clientId: number) {
    const apiUrl = this.getApiUrl(API_ENDPOINTS.REPORT_SERVICE.REPORTS.ReportsByType);
    return this.http
      .post<ApiResponse<Report[]>>(apiUrl, {
        reportTypeId,
        projectId,
        clientId,
      })
      .pipe(
        map((response) => {
          if (!response.success) {
            throw new Error(response.message);
          }
          return response.data;
        })
      );
  }

  findProjectFiles(obj: GetProjectFilesRequest) {
    const apiUrl = this.getApiUrl(API_ENDPOINTS.REPORT_SERVICE.FILES.GetProjectFiles);
    return this.http.post<ApiResponse<Report[]>>(apiUrl, obj);
  }

  getReportTypes(): Signal<ReportType[]> {
    return computed(() => this.reportTypesSignal());
  }

  getClients(): Signal<Client[]> {
    if (!this.versionToggleService.isV2 && this.clientsSignal().length === 0) {
      this.loadClients();
    }
    return computed(() => this.clientsSignal());
  }

  getStates(): Signal<States[]> {
    return computed(() => this.stateSignal());
  }

  getLocations(): Signal<Locations[]> {
    return computed(() => this.locationSignal());
  }

  clearClientDependentData(): void {
    this.reportTypesSignal.set([]);
    this.stateSignal.set([]);
    this.locationSignal.set([]);
  }

  loadReportTypes(clientId: number): void {
    const apiUrl = this.getApiUrl(API_ENDPOINTS.REPORT_SERVICE.REPORTS.ReportType);
    const params = new HttpParams().set('clientId', clientId.toString());
    this.http
      .get<ApiResponse<ReportType[]>>(apiUrl, { params })
      .pipe(
        map((response) => {
          if (!response.success) {
            throw new Error(response.message);
          }
          return response.data;
        })
      )
      .subscribe({
        next: (types) => this.reportTypesSignal.set(types),
        error: (error) => console.error('Error loading report types:', error),
      });
  }

  private loadClients(): void {
    const apiUrl = this.getApiUrl(API_ENDPOINTS.REPORT_SERVICE.REPORTS.Clients);
    this.http
      .get<ApiResponse<Client[]>>(apiUrl)
      .pipe(
        map((response) => {
          if (!response.success) {
            throw new Error(response.message);
          }
          return response.data;
        })
      )
      .subscribe({
        next: (clients) => this.clientsSignal.set(clients),
        error: (error) => console.error('Error loading clients:', error),
      });
  }

  loadStates(clientId: number): void {
    const apiUrl = this.getApiUrl(API_ENDPOINTS.REPORT_SERVICE.REPORTS.States);
    const params = new HttpParams().set('clientId', clientId.toString());
    this.http
      .get<ApiResponse<States[]>>(apiUrl, { params })
      .pipe(
        map((response) => {
          if (!response.success) {
            throw new Error(response.message);
          }
          return response.data;
        })
      )
      .subscribe({
        next: (states) => this.stateSignal.set(states),
        error: (error) => console.error('Error loading states:', error),
      });
  }

  loadLocations(clientId: number): void {
    const apiUrl = this.getApiUrl(API_ENDPOINTS.REPORT_SERVICE.REPORTS.Locations);
    const params = new HttpParams().set('clientId', clientId.toString());
    this.http
      .get<ApiResponse<Locations[]>>(apiUrl, { params })
      .pipe(
        map((response) => {
          if (!response.success) {
            throw new Error(response.message);
          }
          return response.data;
        })
      )
      .subscribe({
        next: (location) => this.locationSignal.set(location),
        error: (error) => console.error('Error loading states:', error),
      });
  }

  sendReportForApproval(request: ReportStatusRequest): Observable<ApiResponse<boolean>> {
    const apiUrl = this.getApiUrl(API_ENDPOINTS.REPORT_SERVICE.REPORTS.ApprovalStatus).replace('{reportId}', request.reportId.toString());
    return this.http.post<ApiResponse<boolean>>(apiUrl, request);
  }

  updateCommentSection(request: CommentRequest): Observable<ApiResponse<boolean>> {
    const apiUrl = this.getApiUrl(API_ENDPOINTS.REPORT_SERVICE.REPORTS.UpdateComments).replace('{reportId}', request.reportId.toString());
    return this.http.post<ApiResponse<boolean>>(apiUrl, request);
  }

  getReportCount(): Observable<ApiResponse<number[]>> {
    const apiUrl = this.getApiUrl(API_ENDPOINTS.REPORT_SERVICE.REPORTS.GetCharts);
    return this.http.get<ApiResponse<number[]>>(apiUrl);
  }

  getSectionList(reportId: number): Observable<ApiResponse<ReportSectionData>> {
    const apiUrl = this.getApiUrl(API_ENDPOINTS.REPORT_SERVICE.REPORTS.GetReportSections).replace(
      '{reportId}',
      reportId.toString()
    );
    return this.http.get<ApiResponse<ReportSectionData>>(apiUrl);
  }

  addNewSection(request: NewSectionRequest): Observable<ApiResponse<boolean>> {
    const apiUrl = this.getApiUrl(API_ENDPOINTS.REPORT_SERVICE.REPORTS.AddReportSection);
    return this.http.post<ApiResponse<boolean>>(apiUrl, request);
  }
  updateSection(request: NewSectionRequest): Observable<ApiResponse<boolean>> {
    const apiUrl = this.getApiUrl(API_ENDPOINTS.REPORT_SERVICE.REPORTS.UpdateReportSections);
    return this.http.post<ApiResponse<boolean>>(apiUrl, request);
  }

  deleteSection(request: NewSectionRequest): Observable<ApiResponse<boolean>> {
    const apiUrl = this.getApiUrl(API_ENDPOINTS.REPORT_SERVICE.REPORTS.DeleteReportSections);
    return this.http.delete<ApiResponse<boolean>>(apiUrl, { body: request });
  }

  addInitialReportData(request: AddReportRequest): Observable<ApiResponse<boolean>> {
    const apiUrl = this.getApiUrl(API_ENDPOINTS.REPORT_SERVICE.REPORTS.CreateReport);
    return this.http.post<ApiResponse<boolean>>(apiUrl, request);
  }

  updateSectionValidationStatus(
    request: SectionValidationRequest
  ): Observable<ApiResponse<boolean>> {
    const apiUrl = this.getApiUrl(API_ENDPOINTS.REPORT_SERVICE.REPORTS.UpdateValidationRequested).replace('{reportId}', request.reportId.toString());
    return this.http.patch<ApiResponse<boolean>>(apiUrl, request);
  }

  getReportGenerationStatus(reportId: number): Observable<ApiResponse<number>> {
    const apiUrl = this.getApiUrl(
      API_ENDPOINTS.REPORT_SERVICE.REPORTS.GenerateReportStatus
    ).replace('{reportId}', reportId.toString());
    return this.http.get<ApiResponse<number>>(apiUrl);
  }

  getReworkSectionList(
    request: SectionValidationRequest
  ): Observable<ApiResponse<ReportSectionData>> {
    const apiUrl = this.getApiUrl(API_ENDPOINTS.REPORT_SERVICE.REPORTS.GetReworkSections);
    return this.http.post<ApiResponse<ReportSectionData>>(apiUrl, request);
  }

  downloadReport(fileId: string = '', filePath: string = '') {
    let apiUrl = this.getApiUrl(API_ENDPOINTS.REPORT_SERVICE.FILES.ReportDownload);
  
    if (fileId) {
      apiUrl += `&fileId=${fileId}`;
    } else if (filePath) {
      apiUrl += `&filePath=${encodeURIComponent(filePath)}`;
    }
  
    return this.http.get(apiUrl, {
      responseType: 'blob',
      observe: 'response'
    });
  }

  getReviewers(): Observable<ApiResponse<Reviewer[]>>{
    let apiUrl = this.getApiUrl(API_ENDPOINTS.REPORT_SERVICE.REVIEWER.GetReviewers);
    return this.http.get<ApiResponse<Reviewer[]>>(apiUrl);
  }

  addReviewer(request: ReviewerRequest): Observable<ApiResponse<boolean>>{
    let apiUrl = this.getApiUrl(API_ENDPOINTS.REPORT_SERVICE.REVIEWER.AddReviewer);
    return this.http.post<ApiResponse<boolean>>(apiUrl, request);
  }

  deleteSectionComment(request: CommentDeleteRequest,reportId: number): Observable<ApiResponse<boolean>> {
    let apiUrl = this.getApiUrl(API_ENDPOINTS.REPORT_SERVICE.REPORTS.DeleteComment).replace('{reportId}', reportId.toString());
    return this.http.delete<ApiResponse<boolean>>(apiUrl, { body: request });
  }
  
}
